import React, { createContext, useCallback, useEffect } from 'react';
import { noop } from 'lodash';
import { getContext } from '@fiverr-private/fiverr_context';
import { trackView, trackAction, Platform, TrackAction, TrackActionProps } from '../../../../lib/mixpanel';

/**
 * Usage:
 *
 * ```tsx
 *  const track = useContext(MixpanelContext);
 *  const callback = () => track(TrackAction.Save);
 * ```
 */

type MixpanelContextValue = (action: TrackAction) => void;

const initialValue = noop satisfies MixpanelContextValue;

export const MixpanelContext = createContext<MixpanelContextValue>(initialValue);

interface MixpanelContextProviderProps
  extends Pick<TrackActionProps, 'deliveryId' | 'gigId' | 'sellerId' | 'itemType' | 'subCategoryId'> {
  children: React.ReactNode;
}

export const MixpanelContextProvider = ({
  deliveryId,
  gigId,
  sellerId,
  itemType,
  subCategoryId,
  children,
}: MixpanelContextProviderProps) => {
  useEffect(() => {
    trackView();
  }, []);

  const track = useCallback(
    (action: TrackAction) => {
      const { isMobile } = getContext();
      const platform = isMobile ? Platform.Mobile : Platform.Desktop;
      trackAction({
        deliveryId,
        gigId,
        sellerId,
        itemType,
        subCategoryId,
        action,
        platform,
      });
    },
    [deliveryId, gigId, sellerId, itemType, subCategoryId]
  );

  return <MixpanelContext.Provider value={track}>{children}</MixpanelContext.Provider>;
};
